<template>
    <BaseModal
        transition-direction="left"
        blur-background
        :is-open="isOpen"
        @close="closeModal">
        <div
            class="absolute top-3 w-full max-w-[18.625rem] transition-all md:top-10">
            <div
                class="relative z-50 transform-gpu rounded-r-20 bg-white px-3 pb-3 pt-3 transition-transform duration-[400ms] before:absolute before:right-full before:top-0 before:size-full before:translate-x-0 before:bg-white">
                <button
                    aria-label="Закрыть контактное меню"
                    class="pl-1.5 pt-2 outline-none focus:outline-none md:pl-3 md:pt-5"
                    @click="closeModal">
                    <AtomIcon
                        name="close"
                        class="size-7 text-accent" />
                </button>
                <div class="mt-5 box-content shrink-0">
                    <ul class="divide-y divide-ash-45">
                        <li
                            v-for="(item, index) in contacts"
                            :key="index"
                            class="space-y-1 py-5 text-white">
                            <p
                                v-html="item.title"
                                class="font-semibold text-ash" />
                            <NuxtLink
                                :to="`tel:${item.phone}`"
                                class="inline-block font-semibold text-accent">
                                {{ formatPhoneNumber(item.phone) }}
                            </NuxtLink>
                            <p
                                v-html="item.address"
                                class="mt-1 text-12/140 font-medium text-ash" />
                        </li>
                        <li class="pt-5 text-16/150 font-semibold text-ash">
                            <p>Электронная почта</p>
                            <div class="flex items-center text-accent">
                                <NuxtLink
                                    :to="`mailto:${readyEmailForCopy}`"
                                    class="max-w-60 break-all font-semibold transition-colors duration-300 hover:text-accent-700">
                                    <span ref="emailCromi">
                                        info@cromi.ru
                                    </span>
                                </NuxtLink>

                                <BaseCopyButton
                                    done-color-accent
                                    size="small"
                                    :text-to-copy="readyEmailForCopy" />
                            </div>
                            <p
                                class="mt-1 max-w-[9.75rem] text-12/116 font-medium">
                                *Адрес специально такой, чтобы было меньше спама
                            </p>
                        </li>
                    </ul>
                    <div class="mt-9 flex w-full flex-col items-start gap-1">
                        <AtomButton
                            appearance="accent"
                            class="h-12 !text-14/148"
                            @click="handleOpenFeedbackModal">
                            Отправить заявку
                        </AtomButton>
                        <div class="flex gap-1">
                            <BaseMessengerLink
                                aria-label="Связаться в ватсап"
                                icon="whatsapp-outline"
                                :link="whatsAppLink" />
                            <BaseMessengerLink
                                aria-label="Связаться в MAX"
                                icon="max-outline"
                                :link="messengers.max.link" />
                            <BaseMessengerLink
                                icon="telegram-outline"
                                aria-label="Связаться в телеграм"
                                :link="messengers.telegram.link" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </BaseModal>
</template>

<script setup lang="ts">
import { useMutationObserver } from '@vueuse/core'
import { contacts, messengers } from '~/const/company-info.consts'

import AtomButton from '~/components/atom/atom-button/AtomButton.vue'
import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'
import BaseCopyButton from '~/components/base/base-copy-button/BaseCopyButton.vue'
import BaseMessengerLink from '~/components/base/base-messenger-link/BaseMessengerLink.vue'
import BaseModal from '~/components/base/base-modal/BaseModal.vue'

import { formatPhoneNumber } from '~/utils/formats'

defineProps({
    isOpen: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
})

const modalStore = useModalStore()
const route = useRoute()

const { data } = await useContactSettings()

const whatsAppLink = computed(() => {
    if (!data.value) return messengers.whatsApp.link

    const api = 'https://api.whatsapp.com/send'
    const phone = String(data.value.data.whatsApp_phone).replace(/^\+7/, '')

    return `${api}?phone=${phone}&text=${data.value.data.whatsApp_message}`
})

const emailCromi = useTemplateRef('emailCromi')
const readyEmailForCopy = ref('info@cromi.ru')

onMounted(() => {
    useMutationObserver(
        emailCromi,
        (mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === 'characterData' ||
                    mutation.type === 'childList'
                ) {
                    readyEmailForCopy.value =
                        emailCromi.value?.textContent || 'info@cromi.ru'
                }
            })
        },
        {
            characterData: true,
            childList: true,
            subtree: true,
        }
    )
})

function closeModal() {
    modalStore.close('contactMenu')
}

function handleOpenFeedbackModal() {
    closeModal()
    modalStore.setFeedbackLabel(modalStore.headerFeedbackLabel)
    modalStore.setFormName('Контактное меню, мобильная версия')

    if (isCromi() && route.name === 'direction-subcategory') {
        modalStore.setFormID(5)
    }

    modalStore.open('feedbackModal')
}
</script>
